import { daysInMonth, isoDay, minorUnitExponent, minorUnitsPerUnit } from "@dentacore/shared-types";

import { formatMoney } from "../patients/money";

/**
 * Dates and money for the Accounting screen (STORY-172).
 *
 * **A date is `YYYY-MM-DD` text throughout, compared as text**, exactly as the
 * shared contract does. "Today" is always the clinic's today from the API
 * (`AccountingYear.today`) and never the browser's clock: a laptop in another
 * timezone would otherwise count a different number of salary days than the
 * server did, and the screen would disagree with its own totals.
 *
 * Names are drawn through `Intl` in UTC, so a date is never shifted a day by
 * the viewer's own timezone.
 */

export interface Ymd {
  year: number;
  /** 1–12. */
  month: number;
  day: number;
}

/** `2026-10-05` → `{ 2026, 10, 5 }`. */
export function parseDay(iso: string): Ymd {
  const [y, m, d] = iso.split("-").map(Number);
  return { year: y ?? 0, month: m ?? 1, day: d ?? 1 };
}

function utc(year: number, month: number, day = 1): Date {
  return new Date(Date.UTC(year, month - 1, day));
}

/** "January". */
export function monthName(month: number): string {
  return utc(2000, month).toLocaleDateString(undefined, { month: "long", timeZone: "UTC" });
}

/** "Jan". */
export function monthShort(month: number): string {
  return utc(2000, month).toLocaleDateString(undefined, { month: "short", timeZone: "UTC" });
}

/** "Mon". */
export function weekdayShort(iso: string): string {
  const { year, month, day } = parseDay(iso);
  return utc(year, month, day).toLocaleDateString(undefined, {
    weekday: "short",
    timeZone: "UTC",
  });
}

/** "5 Oct 2026" (in the viewer's own order). */
export function niceDay(iso: string): string {
  const { year, month, day } = parseDay(iso);
  return utc(year, month, day).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    timeZone: "UTC",
  });
}

/** "5 Oct", for a list already headed by its year. */
export function niceDayShort(iso: string): string {
  const { year, month, day } = parseDay(iso);
  return utc(year, month, day).toLocaleDateString(undefined, {
    day: "numeric",
    month: "short",
    timeZone: "UTC",
  });
}

/** A timestamp from the server, as the viewer's own date and time. */
export function niceMoment(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    day: "numeric",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** Whether a month has begun by `today` — a later month has no figures yet. */
export function monthStarted(year: number, month: number, today: string): boolean {
  return isoDay(year, month, 1) <= today;
}

/** The first and last day of a month, or of the whole year when `month` is 0. */
export function periodRange(year: number, month: number): { from: string; to: string } {
  if (month === 0) return { from: isoDay(year, 1, 1), to: isoDay(year, 12, 31) };
  return { from: isoDay(year, month, 1), to: isoDay(year, month, daysInMonth(year, month)) };
}

/** The first day of the month after `today`'s — a new salary's default (AC-D10). */
export function firstOfNextMonth(today: string): string {
  const { year, month } = parseDay(today);
  return month === 12 ? isoDay(year + 1, 1, 1) : isoDay(year, month + 1, 1);
}

/* ── money ───────────────────────────────────────────────────────────────── */

/** Full money, from minor units — the one formatter the product uses. */
export function money(cents: number, currency: string): string {
  return formatMoney(cents, currency);
}

/**
 * Money shortened for a chart axis — "$12K". **Only ever on an axis or a bar
 * label**, where the exact figure is one click away in the table under it;
 * every table and fact on the screen shows the full amount.
 */
export function shortMoney(cents: number, currency: string): string {
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency,
      notation: "compact",
      maximumFractionDigits: 1,
    }).format(cents / minorUnitsPerUnit(currency));
  } catch {
    return formatMoney(cents, currency);
  }
}

/**
 * Minor units as the text a money box starts with — `650000` → `"6500.00"`
 * in a two-decimal currency, `"6500"` in a zero-decimal one. The inverse of
 * `parseMoney`, so editing an amount and saving it unchanged is a no-op.
 */
export function moneyInput(cents: number, currency: string): string {
  const decimals = minorUnitExponent(currency);
  return (cents / 10 ** decimals).toFixed(decimals);
}

/** "42%", from a fraction. */
export function percent(fraction: number): string {
  return `${String(Math.round(fraction * 100))}%`;
}

/** "98.4%", from a fraction — one decimal, because a rate is judged against a target a point away. */
export function percentOne(fraction: number): string {
  return `${(Math.round(fraction * 1000) / 10).toFixed(1)}%`;
}

/** What an expense category's group is called on screen (AF-D5). */
export const EXPENSE_GROUP_LABELS = {
  SUPPLIES_LAB: "Supplies and lab",
  OCCUPANCY: "Occupancy",
  OTHER: "Other",
} as const;

/** Basis points as the text a percent box starts with: 9800 → "98", 9850 → "98.5". */
export function basisPointsText(basisPoints: number | null): string {
  if (basisPoints === null) return "";
  return String(basisPoints / 100);
}

/**
 * A typed percent as basis points, `null` for a blank box (no target), or
 * `"invalid"`. Between 0.01 and 100, **at most two decimals** — `98.123` would
 * otherwise be saved as 98.12 without a word.
 */
export function parseBasisPoints(typed: string): number | null | "invalid" {
  const text = typed.trim().replace("%", "").trim();
  if (text === "") return null;
  if (!/^\d+(\.\d{1,2})?$/.test(text)) return "invalid";
  const basisPoints = Math.round(Number(text) * 100);
  return basisPoints >= 1 && basisPoints <= 10_000 ? basisPoints : "invalid";
}

/** Minutes as hours to one decimal, trailing ".0" dropped: 90 → "1.5 h", 120 → "2 h". */
export function hoursText(minutes: number): string {
  const hours = Math.round((minutes / 60) * 10) / 10;
  return `${String(hours)} h`;
}

/** "3 expenses" / "1 expense". */
export function plural(count: number, noun: string): string {
  return `${String(count)} ${noun}${count === 1 ? "" : "s"}`;
}

/**
 * What is wrong with a typed amount, or null when it is a usable one.
 *
 * Above zero, and **no more decimal places than the currency has** — `12.345`
 * in dollars would otherwise be rounded to `12.35` without a word, and a
 * figure saved differently from how it was typed is the kind of error this
 * screen exists to avoid. Empty is "not yet", reported as its own sentence so
 * the caller can stay quiet until something has been typed.
 */
export function amountProblem(
  typed: string,
  currency: string,
  options: { zeroAllowed?: boolean } = {},
): string | null {
  const trimmed = typed.trim();
  if (trimmed === "") return "Enter an amount.";
  if (!/^\d+(\.\d+)?$/.test(trimmed)) return "That is not a complete number.";
  const decimals = trimmed.split(".")[1]?.length ?? 0;
  const allowed = minorUnitExponent(currency);
  if (decimals > allowed) {
    return allowed === 0
      ? "Whole amounts only in this currency."
      : `${String(allowed)} decimal places at most.`;
  }
  // A salary may be zero, which records unpaid time (QF-D29); an expense may not.
  if (Number(trimmed) === 0 && options.zeroAllowed !== true) return "Enter an amount above zero.";
  return null;
}
